import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from "react";

import { Button, Checkbox, Modal, Segment } from "semantic-ui-react";

import { RowInfo } from "react-table-6";
import moment from "moment";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SearchBoxDropdown from "react-lib/appcon/common/SearchBoxDropdown";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";
import MultiSelectCheckboxDropdown from "react-lib/appcon/common/MultiSelectCheckboxDropdown"

// UX
import CardPackagePurchaseHistoryUX from "./CardPackagePurchaseHistoryUX";
import CardAppointmentPackage, { CARD_APPOINTMENT_PACKAGE } from "./CardAppointmentPackage";

// Interface
import {
  DEFAULT_LIST_LIMIT,
  MasterDataType,
  MasterOptionsType,
  ORDER_PAYMENT_STATUS,
  PACKAGE_SEARCH_AUH,
  PACKAGE_SEARCH_PH,
  PRODUCT_SEARCH_AUH,
  RunSequence,
  State,
  StatusesFilterType,
  USAGE_STATUS,
} from "./sequence/PackagePurchase";

// Utils
import { formatDatetime } from "react-lib/utils/dateUtils";
import { useIntl } from "react-intl";
import ModConfirm from "../../common/ModConfirm";
import { PackageDetailType } from "./sequence/SettingPackage";
import { customStatusOptions } from "../BIL/BILInterface";

import CONFIG from "config/config";

// Types
type CardPackagePurchaseHistoryProps = {
  onEvent: (e: any) => any;
  setProp: (key: string, value: any, callback?: () => any) => any;
  // seq
  runSequence: RunSequence;
  PackagePurchaseSequence?: State["PackagePurchaseSequence"];
  // data
  disabledOrderIds?: number[];
  selectable?: boolean;
  selectedDivision?: Record<string, any> | null;
  statusesFilter?: StatusesFilterType;
  hidePackageTypeFilter?: boolean;
  hideProgramNameFilter?: boolean;
  hideDivisionFilter?: boolean;
  showPackageNameFilter?: boolean;
  filterByDivision?: boolean;
  packageBinderTitle?: string;
  // CommonInterface
  buttonLoadCheck?: Record<string, any>;
  errorMessage?: Record<string, any>;
  masterData?: MasterDataType;
  searchedItemListWithKey?: Record<string, any>;
  onClickDescription?: (data: PackageDetailType) => void;
  onClickAppointment?: () => void;
  // options
  masterOptions?: MasterOptionsType;
  // callback
  onOpenHistory?: () => any;
  onSelectPackage?: (data: Record<string, any>) => any;
  // config
  isPackageOrder?: boolean;
  hideTitle?: boolean;
  inCartFilter?: boolean;
  // labels (optional overrides)
  confirmHeaderText?: string;
  selectPromptText?: string;
  confirmButtonText?: string;
};

type HistoryDataType = {
  package_code: string;
  package_id: number;
  package_name: string;
  product_code: string;
  product_id: number;
  product_name: string;
};

// Const
const USAGE_OPTIONS = [
  { key: 1, text: "มีรายการคงเหลือ", value: USAGE_STATUS.INCOMPLETED },
  { key: 2, text: "ใช้งานครบ (Done)", value: USAGE_STATUS.COMPLETED },
];

const BUTTON_ACTIONS = {
  print: "PRINT_PACKAGE_ORDER",
  search: "SEARCH",
  select: "SELECT_PACKAGE_ORDER",
  selectPackageBinder: "SELECT_PACKAGE_BINDER",
};

const GridCenter = { display: "grid", placeContent: "center" } as CSSProperties;

const CARD_PACKAGE_PURCHASE_HISTORY = "CardPackagePurchaseHistory";

const ACTION_SELECT = `${CARD_PACKAGE_PURCHASE_HISTORY}_${BUTTON_ACTIONS.select}`;
const ACTION_SEARCH = `${CARD_PACKAGE_PURCHASE_HISTORY}_${BUTTON_ACTIONS.search}`;
const ACTION_PRINT = `${CARD_PACKAGE_PURCHASE_HISTORY}_${BUTTON_ACTIONS.print}`;
const ACTION_SELECT_PACKAGE_BINDER = `${CARD_PACKAGE_PURCHASE_HISTORY}_${BUTTON_ACTIONS.selectPackageBinder}`;

const CardPackagePurchaseHistory = (props: CardPackagePurchaseHistoryProps) => {
  const intl = useIntl();
  const [checkedIds, setCheckedIds] = useState<number[]>([]);

  const isMEHealth = CONFIG.COMPANY === "MEHEALTH";

  // Effect
  useEffect(
    () => () => {
      props.setProp(`searchedItemListWithKey.${PACKAGE_SEARCH_PH}`, []);
    },
    [],
  );

  useEffect(() => {
    const order = props.PackagePurchaseSequence?.selectedPackageOrder;
    const list = props.PackagePurchaseSequence?.purchaseHistoryList || [];

    const findOrder = list.find((item) => item.id === order?.id);

    if (!!order && !findOrder) {
      props.setProp("PackagePurchaseSequence.selectedPackageOrder", null);
    }

    setCheckedIds([]);
  }, [
    props.PackagePurchaseSequence?.purchaseHistoryList,
    props.PackagePurchaseSequence?.selectedPackageOrder,
  ]);

  useEffect(() => {
    if (props.onClickDescription && props.PackagePurchaseSequence?.selectedPackageOrder) {
      props.setProp("PackagePurchaseSequence.selectedPackageOrder", null);

      props.onClickDescription(props.PackagePurchaseSequence?.selectedPackageOrder);
    }
  }, [props.PackagePurchaseSequence?.selectedPackageOrder]);

  // Memo callback
  const filterPurchaseHistory = useMemo(
    () => props.PackagePurchaseSequence?.filterPurchaseHistory || {},
    [props.PackagePurchaseSequence?.filterPurchaseHistory],
  );

  // Use Callback
  const handleSelectedItem = useCallback(
    async (value: any) => {
      props.setProp("PackagePurchaseSequence.filterPurchaseHistory", {
        ...filterPurchaseHistory,
        product: value || null,
      });
    },
    [props.searchedItemListWithKey],
  );

  const handleClickHistory = useCallback(async (data: Partial<HistoryDataType>) => {
    const packageId = data.package_id;
    const productId = data.product_id;

    const initialFilter = async (id: string, key: "package" | "product") => {
      props.setProp(`searchedItemListWithKey.${id}`, [
        {
          id: data[`${key}_id`],
          code: data[`${key}_code`],
          name: data[`${key}_name`],
        },
      ]);

      props.setProp("PackagePurchaseSequence.filterAppointmentHistory", {
        [key]: data[`${key}_id`],
      });
    };

    if (packageId) {
      await initialFilter(PACKAGE_SEARCH_AUH, "package");
    } else if (productId) {
      await initialFilter(PRODUCT_SEARCH_AUH, "product");
    }

    props.onOpenHistory?.();
  }, []);

  const handleSelectRow = useCallback(
    (data: any, index: number) => {
      if (data) {
        props.runSequence({
          sequence: "PackagePurchase",
          action: "SELECT_PACKAGE_ORDER",
          card: CARD_PACKAGE_PURCHASE_HISTORY,
          index,
          isFetchDetail: props.selectable || !!props.onClickDescription,
          orderId: data.id,
          productId: data.product,
        });
      } else {
        props.setProp("PackagePurchaseSequence.selectedPackageOrder", null);
      }
    },
    [props.PackagePurchaseSequence?.purchaseHistoryList, props.onClickDescription],
  );

  const handlePrint = (data: any, index: number) => {
    if (data) {
      props.runSequence({
        sequence: "PackagePurchase",
        action: "PRINT_PACKAGE_ORDER",
        card: CARD_PACKAGE_PURCHASE_HISTORY,
        index,
        orderId: data.id,
      });
    }
  };

  const handleSelectPackageBinder = (data: any, index: number, isAppointment?: boolean) => {
    if (data) {
      props.runSequence({
        sequence: "PackagePurchase",
        action: "SELECT_PACKAGE_BINDER",
        card: CARD_PACKAGE_PURCHASE_HISTORY,
        index,
        orderId: data.id,
        isAppointment,
      });
    }
  };

  const handleOrderPackageBinder = (data: any, check: number[], card: string) => {
    props.runSequence({
      sequence: "PackagePurchase",
      action: "ORDER_PACKAGE_BINDER",
      card: card || CARD_PACKAGE_PURCHASE_HISTORY,
      data,
      check,
      onSuccess: () => {
        setCheckedIds([]);
      },
    });
  };

  const handleSelectPackageGroupBinder = (isAppointment?: boolean) => {
    if (checkedIds) {
      props.runSequence({
        sequence: "PackagePurchase",
        action: "SELECT_PACKAGE_GROUP_BINDER",
        card: CARD_PACKAGE_PURCHASE_HISTORY,
        orderData: checkedIds,
        isAppointment,
      });
    }
  };

  const handleClosePackageBinder = () => {
    props.setProp(`PackagePurchaseSequence.selectPackageBinder`, {});
  };

  // Use Memo
  const serviceDivisionOptions = useMemo(() => {
    const divisions = new Set<number>(
      (props.masterData?.packageServiceType || []).flatMap((item) =>
        (item.divisions as number[]).map(Number),
      ),
    );

    const options = (props.masterOptions?.division || []).filter((option) =>
      divisions.has(Number(option.value)),
    );

    if (props.selectedDivision?.id) {
      const { id } = props.selectedDivision;

      options.push({
        key: id,
        text: props.selectedDivision.name,
        value: id,
      });
    }

    return [...new Map(options.map((item) => [item.value, item])).values()];
  }, [
    props.masterOptions?.division,
    props.masterOptions?.packageServiceType,
    props.selectedDivision,
  ]);

  const searchedItemListWithKey = useMemo(() => {
    const items: Record<string, any>[] = props.searchedItemListWithKey?.[PACKAGE_SEARCH_PH] || [];

    return {
      ...props.searchedItemListWithKey,
      [PACKAGE_SEARCH_PH]: items.map((item) => ({
        ...item,
        name_code: `[${item.code}] ${item.name}`,
      })),
    };
  }, [props.searchedItemListWithKey]);

  const selectedPackageOrder = useMemo(() => {
    const order = props.PackagePurchaseSequence?.selectedPackageOrder;
    const items: Record<string, any>[] = order?.items || [];

    if (order) {
      order.items = items.map((item) => ({
        ...item,
        balance: Number(item.quantity_left),
      }));
    }

    return order;
  }, [props.PackagePurchaseSequence?.selectedPackageOrder]);

  const allowedCancel = useMemo(
    () =>
      selectedPackageOrder?.usage_status === USAGE_STATUS.INCOMPLETED &&
      selectedPackageOrder.status === ORDER_PAYMENT_STATUS.BILLED,
    [selectedPackageOrder],
  );

  const isAppointmentSelectable = useMemo(
    () => props.selectable && selectedPackageOrder?.usage_status !== USAGE_STATUS.COMPLETED,
    [props.selectable],
  );

  const handleChecked = (checked: boolean | undefined, item: any) => {
    const ids: number[] = [item.id];

    if (checkedIds.some((id) => ids.includes(id))) {
      setCheckedIds(checkedIds.filter((id) => !ids.includes(id)));
    } else {
      let newCheckedIds = [...checkedIds, ...ids];
      setCheckedIds(newCheckedIds);
    }
  };

  const customStatusOption = customStatusOptions.map((option) => ({
    ...option,
    content: (
      <div className="dropdown-option">
        <Checkbox
          checked={(filterPurchaseHistory?.customStatus || []).includes(option.value)}
          onChange={() => {}}
        />
        <span>{option.text}</span>
      </div>
    ),
  }));

  const purchaseHistoryItems = useMemo(
    () =>
      (props.PackagePurchaseSequence?.purchaseHistoryList || []).map((item, index) => {
        const payments = {
          BILLED: "รอรับชำระ",
          PAID: "ชำระแล้ว",
        } as any;

        let color = "#1B9D2C";

        if (props.disabledOrderIds?.includes(item.id || "")) {
          color = "#BDBDBD";
        } else if (item.usage_status === USAGE_STATUS.INCOMPLETED) {
          color = "#DA0000";
        }

        const appointmentDatetime = item?.appointment_datetime
          ? formatDatetime(moment(item.appointment_datetime))
          : "";

        return {
          ...item,
          chk: (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Checkbox
                checked={checkedIds.includes(item.id)}
                disabled={item.usage_status === USAGE_STATUS.COMPLETED}
                onChange={(e, v) => {
                  handleChecked(v.checked, item);
                }}
              />
            </div>
          ),
          date: formatDatetime(moment(item.created_at)),
          appointment_datetime: appointmentDatetime,
          description: (
            <div style={GridCenter}>
              <div style={{ display: "flex" }}>
                {!props.showPackageNameFilter && (
                  <Button
                    style={{
                      backgroundColor: "#fbbd08",
                      fontSize: "0.75rem",
                      padding: "0.60rem 0.75rem",
                    }}
                    size="mini"
                    disabled={item.usage_status === USAGE_STATUS.COMPLETED}
                    onClick={(e) => {
                      e.stopPropagation();

                      handleSelectPackageBinder(item, index, true);

                      props.onClickAppointment?.();
                    }}
                  >
                    ทำนัดหมาย
                  </Button>
                )}
                <Button
                  color="blue"
                  disabled={color === "#BDBDBD"}
                  loading={!!props.buttonLoadCheck?.[`${ACTION_SELECT}_${index}`]}
                  size="mini"
                  style={{ fontSize: "0.75rem", padding: "0.60rem 0.75rem" }}
                  // callback
                  onClick={(e) => {
                    e.stopPropagation();

                    handleSelectRow(item, index);
                  }}
                >
                  รายละเอียด
                </Button>
                {props.showPackageNameFilter && (
                  <Button
                    loading={!!props.buttonLoadCheck?.[`${ACTION_PRINT}_${index}`]}
                    size="mini"
                    style={{
                      background: "#2688D1",
                      color: "white",
                      fontSize: "0.75rem",
                      padding: "0.60rem 0.75rem",
                    }}
                    // callback
                    onClick={(e) => {
                      e.stopPropagation();

                      handlePrint(item, index);
                    }}
                  >
                    พิมพ์รายละเอียด
                  </Button>
                )}
              </div>
            </div>
          ),
          print: (
            <div style={GridCenter}>
              {props.isPackageOrder ? (
                <Button
                  color="green"
                  disabled={color === "#BDBDBD" || item?.usage_status === USAGE_STATUS.COMPLETED}
                  loading={!!props.buttonLoadCheck?.[`${ACTION_SELECT_PACKAGE_BINDER}_${index}`]}
                  size="mini"
                  style={{ fontSize: "0.75rem", padding: "0.60rem 0.75rem" }}
                  // callback
                  onClick={(e) => {
                    e.stopPropagation();

                    handleSelectPackageBinder(item, index);
                  }}
                >
                  {props.packageBinderTitle || "ส่งคำสั่งแพทย์"}
                </Button>
              ) : (
                <Button
                  color="blue"
                  disabled={color === "#BDBDBD"}
                  loading={!!props.buttonLoadCheck?.[`${ACTION_PRINT}_${index}`]}
                  size="mini"
                  style={{ fontSize: "0.75rem", padding: "0.60rem 0.75rem" }}
                  // callback
                  onClick={(e) => {
                    e.stopPropagation();

                    handlePrint(item, index);
                  }}
                >
                  พิมพ์รายละเอียด
                </Button>
              )}
            </div>
          ),
          history: (
            <div style={GridCenter}>
              <Button
                color="grey"
                icon="history"
                size="mini"
                style={{ fontSize: "0.75rem" }}
                onClick={(e) => {
                  e.stopPropagation();

                  handleClickHistory({
                    package_code: item.product_code,
                    package_id: item.product,
                    package_name: item.product_name,
                  });
                }}
              />
            </div>
          ),
          payment: (
            <div>
              {isMEHealth ? (
                <div
                  style={{
                    color: getStatusColor(item?.custom_status_label),
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  {item?.custom_status_label}
                </div>
              ) : (
                <div style={GridCenter}>{payments[item.status]}</div>
              )}
            </div>
          ),
          price: (
            <div style={GridCenter}>
              {Number(item.price).toLocaleString("en-US", {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
                style: "decimal",
              })}
            </div>
          ),
          quantity: <div style={GridCenter}>{item.quantity}</div>,
          usage: (
            <div style={{ ...GridCenter, color }}>
              {USAGE_OPTIONS?.find((data) => data.value === item.usage_status)?.text}
            </div>
          ),
        };
      }),
    [
      props.buttonLoadCheck,
      props.disabledOrderIds,
      props.PackagePurchaseSequence?.purchaseHistoryList,
      checkedIds,
      isMEHealth,
    ],
  );

  // Handler
  const handleChangeValue = (e: any, data: any) => {
    const value = data.value === undefined ? data.checked : data.value;

    const detail = {
      ...props.PackagePurchaseSequence?.filterPurchaseHistory,
    };

    (detail as any)[data.name] = value;

    props.setProp("PackagePurchaseSequence.filterPurchaseHistory", {
      ...detail,
    });
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "PackagePurchase",
      action: "SEARCH",
      card: CARD_PACKAGE_PURCHASE_HISTORY,
      statusesFilter: props.statusesFilter,
      inCartFilter: props.inCartFilter,
      filterByDivision: props.filterByDivision,
    });
  };

  const handleClear = () => {
    if (props.selectable) {
      const currentFilter = props.PackagePurchaseSequence?.filterPurchaseHistory || {};

      props.setProp("PackagePurchaseSequence.filterPurchaseHistory", {
        division: currentFilter.division,
      });

      return;
    }

    // ถ้า selectable เป็นเท็จ ให้ล้างค่าทั้งหมด
    props.setProp("PackagePurchaseSequence.filterPurchaseHistory", {});
  };

  const handleGetTrProps = (state: any, rowInfo?: RowInfo) => {
    const disabled = props.disabledOrderIds?.includes(rowInfo?.original?.id || "");

    return {
      // onClick: () => {
      //   if (!disabled) {
      //     handleSelectRow(rowInfo);
      //   }
      // },

      className:
        !!selectedPackageOrder?.id && rowInfo?.original?.id === selectedPackageOrder.id
          ? "blueSelectedRow"
          : "",
      style: {
        color: disabled ? "#BDBDBD" : "",
      },
    };
  };

  const handleSelectPackage = (data: any) => {
    props.onSelectPackage?.({
      ...selectedPackageOrder,
      items: data,
    });
  };

  const handleCancelPackage = (reason: any) => {
    props.runSequence({
      sequence: "PackagePurchase",
      action: "CANCEL_PACKAGE",
      card: CARD_APPOINTMENT_PACKAGE,
      data: {
        orderId: selectedPackageOrder?.id,
        packageId: selectedPackageOrder?.product,
        quantity: selectedPackageOrder?.quantity,
        reason,
      },
      statusesFilter: props.statusesFilter,
      inCartFilter: props.inCartFilter,
    });
  };

  const handleCloseSelectOrder = () => {
    props.setProp("PackagePurchaseSequence.selectedPackageOrder", null);
  };

  const handlePageChange = (activePage: number) => {
    props.runSequence({
      sequence: "PackagePurchase",
      action: "SEARCH",
      card: CARD_PACKAGE_PURCHASE_HISTORY,
      statusesFilter: props.statusesFilter,
      inCartFilter: props.inCartFilter,
      filterByDivision: props.filterByDivision,
      activePage,
    });
  };

  const purchaseHistoryHeaders = !props.showPackageNameFilter
    ? `วันที่สั่งซื้อ,รหัส,ชื่อแพ็กเกจ,ราคา,QTY,การชำระเงิน,การใช้งาน,ประวัติ,,`
    : "";

  const purchaseHistoryKeys = !props.showPackageNameFilter
    ? `date,product_code,product_name,price,quantity,payment,usage,history,description,print`
    : "";

  const purchaseHistoryWidths = !props.showPackageNameFilter
    ? `^110,^110,^180,^95,^75,^100,^120,75,100,115`
    : "";

  const handleCheckAll = (e: any, v: any) => {
    if (v.checked) {
      let allId = (purchaseHistoryItems || [])
        .filter((item: any) => item.usage_status !== USAGE_STATUS.COMPLETED)
        .map((item: any) => {
          return item.id;
        });

      setCheckedIds(allId);
    } else {
      setCheckedIds([]);
    }
  };

  const purchaseHistoryColumns = props.showPackageNameFilter && [
    {
      Header: (
        <Checkbox
          checked={
            checkedIds.length ===
              (purchaseHistoryItems || []).filter(
                (item: any) => item.usage_status !== USAGE_STATUS.COMPLETED,
              ).length && checkedIds.length > 0
          }
          onChange={handleCheckAll}
        />
      ),
      width: 60,
      accessor: "chk",
    },
    {
      Header: "วันที่สั่งซื้อ",
      width: 115,
      accessor: "date",
    },
    {
      Header: "รหัส",
      width: 110,
      accessor: "product_code",
    },
    {
      Header: "ชื่อแพ็กเกจ (EN)",
      width: 180,
      accessor: "product_name_en",
    },
    {
      Header: "ชื่อแพ็กเกจ (TH)",
      width: 180,
      accessor: "product_name",
    },
    {
      Header: "ราคา",
      width: 95,
      accessor: "price",
    },
    {
      Header: "QTY",
      width: 75,
      accessor: "quantity",
    },
    {
      Header: "การชำระเงิน",
      width: 200,
      accessor: "payment",
    },
    {
      Header: "วันที่นัดหมาย",
      width: 100,
      accessor: "appointment_datetime",
    },
    {
      Header: "การใช้งาน",
      width: 130,
      accessor: "usage",
    },
    {
      Header: "สถานะในหน้าจอของผู้รับบริการ",
      width: 130,
      accessor: "status_in_mobile_label",
    },
    {
      Header: "ประวัติ",
      width: 95,
      accessor: "history",
    },
    {
      Header: "",
      width: 200,
      accessor: "description",
    },
  ];

  console.log("CardPackagePurchaseHistoryUX", props, selectedPackageOrder);

  return (
    <div>
      <SnackMessage
        onEvent={props.onEvent}
        error={props.errorMessage?.[CARD_APPOINTMENT_PACKAGE]}
        success={null}
        onClose={() => {
          props.setProp(`errorMessage.${CARD_APPOINTMENT_PACKAGE}`, null);
        }}
        languageUX={props.languageUX}
      />

      <CardPackagePurchaseHistoryUX
        // data
        isMEHealth={isMEHealth}
        purchaseHistoryHeaders={purchaseHistoryHeaders}
        purchaseHistoryKeys={purchaseHistoryKeys}
        purchaseHistoryWidths={purchaseHistoryWidths}
        purchaseHistoryColumns={purchaseHistoryColumns}
        filterPurchaseHistory={filterPurchaseHistory}
        purchaseHistoryItems={purchaseHistoryItems}
        selectable={props.selectable}
        packageServiceTypeOptions={props.masterOptions?.packageServiceType}
        hideTitle={props.hideTitle}
        hidePackageTypeFilter={props.hidePackageTypeFilter}
        hideProgramNameFilter={props.hideProgramNameFilter}
        hideDivisionFilter={props.hideDivisionFilter}
        showPackageNameFilter={props.showPackageNameFilter}
        // options
        packageTypeOptions={props.masterOptions?.packageType}
        serviceDivisionOptions={serviceDivisionOptions}
        usageOptions={USAGE_OPTIONS}
        customStatusOptions={customStatusOption}
        // table
        getTrProps={handleGetTrProps}
        // callback
        onChangeValue={handleChangeValue}
        onClear={handleClear}
        // Element
        ButtonSearch={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            // config
            color={"blue"}
            name={BUTTON_ACTIONS.search}
            // data
            paramKey={ACTION_SEARCH}
            size="medium"
            title={intl.formatMessage({ id: "ค้นหา" })}
            buttonLoadCheck={props.buttonLoadCheck?.[ACTION_SEARCH]}
            onClick={handleSearch}
          />
        }
        // SearchBoxPackageType={
        //   <SearchBoxDropdown
        //     onEvent={props.onEvent}
        //     // config
        //     type="PackageType"
        //     id="1"
        //     style={{ width: "100%" }}
        //     fluid={true}
        //     useWithKey={true}
        //     icon="search"
        //     limit={20}
        //     // Select
        //     searchedItemListWithKey={props.searchedItemListWithKey}
        //     selectedItem={null}
        //     setSelectedItem={handleSelectedItem}
        //     // options
        //     mapOptions={mapPackageOptions}
        //   />
        SearchBoxPackageName={
          <SearchBoxDropdown
            onEvent={props.onEvent}
            id="PH"
            icon="search"
            limit={20}
            selectedItem={filterPurchaseHistory.product || null}
            style={{ width: "100%" }}
            // config
            type="Package"
            fluid
            inline
            useWithKey
            // Select
            searchedItemListWithKey={searchedItemListWithKey}
            setSelectedItem={handleSelectedItem}
            onAdditionalUrlParams={() => ({ active: true })}
          />
        }
        customStatus={
          <MultiSelectCheckboxDropdown
            options={customStatusOption}
            value={filterPurchaseHistory?.customStatus}
            onChange={handleChangeValue}
            name="customStatus"
          />
        }
        languageUX={props.languageUX}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          margin: "1rem",
        }}
      >
        <div>
          <ButtonLoadCheck
            setProp={props.setProp}
            paramKey={`${ACTION_SELECT_PACKAGE_BINDER}`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${ACTION_SELECT_PACKAGE_BINDER}`]}
            disabled={checkedIds.length === 0}
            //
            size="small"
            style={{
              backgroundColor: "rgb(255, 189, 97)",
              color: "white",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleSelectPackageGroupBinder(true);
              props.onClickAppointment?.();
            }}
            title={"ทำนัดหมาย"}
          />
          <ButtonLoadCheck
            setProp={props.setProp}
            paramKey={`${ACTION_SELECT_PACKAGE_BINDER}`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${ACTION_SELECT_PACKAGE_BINDER}`]}
            disabled={checkedIds.length === 0}
            //
            size="small"
            style={{
              backgroundColor: "var(--primary-theme-color)",
              color: "white",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleSelectPackageGroupBinder();
            }}
            title={props.packageBinderTitle || "ส่งคำสั่งแพทย์"}
          />
        </div>
        <div>
          <PaginationShorthand
            activePage={props.PackagePurchaseSequence?.purchaseHistoryActivePage || 1}
            limit={DEFAULT_LIST_LIMIT}
            total={props.PackagePurchaseSequence?.purchaseHistoryTotal || 0}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      {selectedPackageOrder?.id && !props.onClickDescription && (
        <div style={{ paddingBottom: props.selectable ? "0" : "2rem" }}>
          <CardAppointmentPackage
            setProp={props.setProp}
            // data
            data={selectedPackageOrder.items}
            // config
            isCancel={allowedCancel}
            selectable={isAppointmentSelectable}
            showQty={props.selectable}
            usageLimit={selectedPackageOrder.usage_limit}
            // CommonInterface
            buttonLoadCheck={props.buttonLoadCheck}
            // options
            masterOptions={props.masterOptions}
            // callback
            onCancel={handleCancelPackage}
            onClose={handleCloseSelectOrder}
            onHistory={handleClickHistory}
            onSelect={handleSelectPackage}
            languageUX={props.languageUX}
          />
        </div>
      )}

      <Modal
        open={
          (props.PackagePurchaseSequence?.selectPackageBinder?.items || [])?.length > 0 &&
          !props.PackagePurchaseSequence?.selectPackageBinder?.isAppointment
        }
        closeOnDimmerClick={true}
        size="large"
        style={{ width: "auto" }}
        onClose={handleClosePackageBinder}
      >
        <Segment
          inverted
          className={"modHeader"}
          style={{
            backgroundColor: "#2185d0",
            lineHeight: "1.3em",
            ...(props.onClickDescription && { backgroundColor: "var(--primary-theme-color)" }),
          }}
        >
          {props.confirmHeaderText || "ยืนยันการส่งรายการคำสั่งแพทย์"}
        </Segment>
        <CardAppointmentPackage
          setProp={props.setProp as any}
          // data
          data={props.PackagePurchaseSequence?.selectPackageBinder?.items || []}
          buttonLoadCheck={props.buttonLoadCheck}
          // config
          selectable={true}
          isEdit={true}
          hideHistory={true}
          showQty={true}
          isHideQty={false}
          isPackageBinder={true}
          // labels
          selectPromptText={props.selectPromptText}
          confirmButtonText={props.confirmButtonText}
          // callback
          onClose={handleClosePackageBinder}
          onApprove={handleOrderPackageBinder}
          languageUX={props.languageUX}
        />
      </Modal>
    </div>
  );
};

CardPackagePurchaseHistory.displayName = "CardPackagePurchaseHistory";

export default React.memo(CardPackagePurchaseHistory);

const getStatusColor = (status?: string) => {
  const statusColors: Record<string, string> = {
    "รอรับชำระ (Staff)": "rgb(31, 124, 140)",
    ชำระแล้ว: "rgb(46, 125, 79)",
    รอชำระเงินหน้าเคาน์เตอร์: "rgb(179, 114, 0)",
    รอตรวจสอบการชำระเงิน: "rgb(176, 65, 62)",
    อยู่ในตะกร้า: "rgb(91, 104, 117)",
    ยกเลิกแล้ว: "rgb(91, 104, 117)",
  };

  return statusColors[status || ""] || "rgb(91, 104, 117)";
};
